import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: string;
  schoolId?: string;
  schoolSlug?: string;
  /** Set for organization-admin sessions. */
  organizationId?: string;
  /** Auth scope: 'superadmin' | 'tenant' | 'school' | 'organization' | 'account'. */
  scope?: string;
  /** Only meaningful for `scope: 'school'` (org-admin-acting-as-school) sessions — whether this school's tenant schema actually has real data yet. Most real schools are `true`; a small number of legacy-migrated schools are still `false` mid-migration. */
  isSchemaProvisioned?: boolean;
}

/** A school a multi-school account may enter (drives the in-app switcher). */
export interface AccountSchool {
  schoolId: string;
  code: string;
  slug: string;
  name: string;
  role: string;
  status: string;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  schoolSlug: string | null;

  /** Multi-school context — kept alongside the active tenant session so the
   * switcher can re-select another school without a fresh login. */
  accountToken: string | null;
  accountSchools: AccountSchool[] | null;

  /** Stashed org token — kept while an org admin is acting as a school, so
   * the switcher/header can switch schools or exit back to the org session
   * without a fresh login. The org's school list is fetched live (it can be
   * 50+ and change over time), never stored here. */
  orgToken: string | null;
  orgRefreshToken: string | null;

  login: (params: {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
    schoolSlug?: string;
  }) => void;

  /** Enter a school WITHOUT clearing the multi-school / org origin context. */
  enterSchoolSession: (params: {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
    schoolSlug: string;
  }) => void;

  setAccountContext: (
    accountToken: string | null,
    accountSchools: AccountSchool[] | null,
  ) => void;

  /** Enter (or return to) an organization-scoped session — used by both org
   * login and exit-school, since both hand back the same shape. */
  setOrgSession: (params: {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
  }) => void;

  setTokens: (accessToken: string, refreshToken: string) => void;
  setSchool: (slug: string) => void;
  setUser: (user: AuthUser | null) => void;
  logout: () => void;

  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      schoolSlug: null,
      accountToken: null,
      accountSchools: null,
      orgToken: null,
      orgRefreshToken: null,

      // A plain login (school / superadmin / org) clears any origin context so
      // a stale switcher can't leak between sessions.
      login: ({ user, accessToken, refreshToken, schoolSlug }) =>
        set({
          user,
          accessToken,
          refreshToken,
          schoolSlug: schoolSlug ?? user.schoolSlug ?? get().schoolSlug,
          accountToken: null,
          accountSchools: null,
          orgToken: null,
          orgRefreshToken: null,
        }),

      enterSchoolSession: ({ user, accessToken, refreshToken, schoolSlug }) =>
        set({ user, accessToken, refreshToken, schoolSlug }),

      setAccountContext: (accountToken, accountSchools) =>
        set({ accountToken, accountSchools }),

      setOrgSession: ({ user, accessToken, refreshToken }) =>
        set({
          user,
          accessToken,
          refreshToken,
          schoolSlug: null,
          orgToken: accessToken,
          orgRefreshToken: refreshToken,
          accountToken: null,
          accountSchools: null,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      setSchool: (slug) => set({ schoolSlug: slug }),

      setUser: (user) => set({ user }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          schoolSlug: null,
          accountToken: null,
          accountSchools: null,
          orgToken: null,
          orgRefreshToken: null,
        }),

      isAuthenticated: () => !!get().accessToken,
    }),
    {
      name: 'edupro-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        schoolSlug: state.schoolSlug,
        accountToken: state.accountToken,
        accountSchools: state.accountSchools,
        orgToken: state.orgToken,
        orgRefreshToken: state.orgRefreshToken,
      }),
    },
  ),
);
